import * as Sentry from "@sentry/browser";
import { useQuery } from "@tanstack/react-query";
import ahoy from "ahoy.js";
import React, { createContext, type PropsWithChildren, useContext, useRef } from "react";
import { AnalyticsEvent, track } from "../analytics/track";
import { getSearchConfig } from "../api";
import AppError from "../components/error";
import { useOnMounted } from "../hooks/mounted";
import { type SearchConfig, SearchConfigSchema } from "../types";

// Constants

const DEFAULT_ROOT_URL: string = import.meta.env.PROD ? "https://app.askdewey.co" : "http://localhost:3000";

interface ConfigProviderProps extends PropsWithChildren {
  apiToken: string;
  rootURL?: string;
  shadowRoot: ShadowRoot | null;
  triggerId: string | undefined;
}

// Config Context Provider

export const ConfigContext = createContext<SearchConfig | undefined>(undefined);

export const ConfigProvider = ({ children, apiToken, rootURL, shadowRoot, triggerId }: ConfigProviderProps) => {
  const $rootURL = rootURL ?? DEFAULT_ROOT_URL;
  const isInitialized = useRef(false);

  useOnMounted(() => {
    ahoy.configure({
      urlPrefix: $rootURL,
    });
  });

  // Load the search config from the server
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["getSearchConfig", $rootURL, apiToken],
    queryFn: getSearchConfig,
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  // Don't display anything during loading
  if (isLoading) {
    return null;
  }

  // Show a generic error if this call fails (something bad has happened)
  if (isError || !data) {
    console.error("Dewey Search failed to load:", error);
    Sentry.captureException(error);
    return <AppError triggerId={triggerId} />;
  }

  try {
    // Validate that the config matches our expected schema
    const validatedConfig = SearchConfigSchema.parse(data);
    const config: SearchConfig = {
      ...validatedConfig,
      apiToken,
      rootURL: $rootURL,
      triggerId: triggerId,
    };

    if (!isInitialized.current) {
      if (import.meta.env.DEV) {
        console.log("Dewey Search initialized:", config);
      }

      // Apply custom styles to the shadow DOM
      updateStyles(config, shadowRoot);

      // Have to explicitly check for excludeFromAnalytics because
      // we're not using the useAnalytics hook here
      if (!config.excludeFromAnalytics) {
        track(AnalyticsEvent.SEARCH_INITIALIZED, config.organization);
      }

      isInitialized.current = true;
    }

    // Provide the Config with our token to the rest of the App
    return <ConfigContext.Provider value={config}>{children}</ConfigContext.Provider>;
  } catch (error) {
    console.error("Dewey Search returned an invalid config:", error);
    Sentry.captureException(error);
    return <AppError triggerId={triggerId} />;
  }
};

// Config Context Hook

export const useConfig = (): SearchConfig => {
  const context = useContext(ConfigContext);
  if (context === undefined) {
    throw new Error("useConfig cannot be used outside of a ConfigProvider");
  }

  return context;
};

function updateStyles(config: SearchConfig, shadowRoot: ShadowRoot | null) {
  if (!shadowRoot) {
    console.error("Dewey Search failed to update styles: no ShadowRoot provided");
    return;
  }

  const host = shadowRoot.host;
  if (!(host instanceof HTMLElement)) {
    console.error("Dewey Search failed to update styles: host is not an HTMLElement");
    return;
  }

  // Set CSS custom properties directly on the host element
  host.style.setProperty("--dewey-search-background-color", config.conversationContext.styles.backgroundColor);
  host.style.setProperty("--dewey-search-foreground-color", config.conversationContext.styles.foregroundColor);
  host.style.setProperty("--dewey-search-link-color", config.conversationContext.styles.linkColor);
  host.style.setProperty("--dewey-search-link-hover-color", config.conversationContext.styles.linkHoverColor);
  host.style.setProperty(
    "--dewey-search-question-bubble-background-color",
    config.conversationContext.styles.questionBubbleBackgroundColor,
  );
  host.style.setProperty(
    "--dewey-search-question-bubble-background-hover-color",
    config.conversationContext.styles.questionBubbleBackgroundHoverColor,
  );
  host.style.setProperty(
    "--dewey-search-question-bubble-foreground-color",
    config.conversationContext.styles.questionBubbleForegroundColor,
  );
  host.style.setProperty(
    "--dewey-search-question-bubble-border-color",
    config.conversationContext.styles.questionBubbleBorderColor,
  );
  host.style.setProperty("--dewey-search-font-family", config.conversationContext.styles.fontFamily || "");
  host.style.setProperty("--dewey-search-font-family-fallback", config.conversationContext.styles.fontFamilyFallback);
  host.style.setProperty("--dewey-search-font-weight", config.conversationContext.styles.fontWeight.toString());
  host.style.setProperty("--dewey-search-header-font-family", config.conversationContext.styles.headerFontFamily || "");
  host.style.setProperty(
    "--dewey-search-header-font-family-fallback",
    config.conversationContext.styles.headerFontFamilyFallback,
  );
  host.style.setProperty(
    "--dewey-search-header-font-weight",
    config.conversationContext.styles.headerFontWeight.toString(),
  );
}
